import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { useLogger } from 'ui-components/src/hooks/useLogger';

import { UserApplicationRole } from '../../constants';
import { useAuth0 } from '@auth0/auth0-react';
import { useLoggedIn } from '../../graphql/community/hooks/useLoggedIn';
import { ConfigContext } from '../ConfigContext';
import { ErrorView } from '../../components/ErrorView';
import { LoadingView } from '../../components/LoadingView';
import { NotAuthorizedView } from '../../components/NotAuthorizedView';

const RoleGuardContext = createContext<RoleGuardContextValue | null>(null);

export const useRoleGuardContext = () => {
  const context = useContext(RoleGuardContext);

  if (!context)
    throw new Error(
      'useRoleGuardContext can only be used inside RoleGuardProvider',
    );

  return context as RoleGuardContextValue;
};

export const RoleGuardProvider: React.FC<RoleGuardProviderProps> = ({
  requiredRoles,
  children,
}) => {
  const logger = useLogger();
  const { isAuthenticated, logout } = useAuth0();
  const { data, loading, error } = useLoggedIn(isAuthenticated);
  const config = ConfigContext.useContainer();

  useEffect(() => {
    if (error) logger.error(error.message);
  }, [error, logger]);

  const value = useMemo(
    () => ({
      roles: (data?.loggedIn?.roles as UserApplicationRole[]) || [],
      userHasRole: (requiredRole: string) =>
        !!data?.loggedIn?.roles?.includes(requiredRole),
      userHasRoles: (requiredRoles: string[]) =>
        requiredRoles.every((role) => data?.loggedIn?.roles?.includes(role)),
    }),
    [data],
  );

  if ((!data && !error) || loading || !isAuthenticated) return <LoadingView />;

  if (error)
    return (
      <ErrorView
        onLogout={() =>
          logout({ logoutParams: { returnTo: config.auth0.logout_uri } })
        }
      />
    );

  if (!value.userHasRoles(requiredRoles)) return <NotAuthorizedView />;

  return (
    <RoleGuardContext.Provider value={value}>
      {children}
    </RoleGuardContext.Provider>
  );
};

export type RoleGuardContextValue = {
  roles: UserApplicationRole[];
  userHasRole: (role: string) => boolean;
  userHasRoles: (roles: string[]) => boolean;
};

export interface RoleGuardProviderProps {
  requiredRoles: UserApplicationRole[];
}
